import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CheckCircle, Briefcase, ArrowsLeftRight, ChatCircle, LinkSimple } from '@phosphor-icons/react';
import { useAuth } from '@/context/AuthContext';
import { useCan } from '@/lib/permissions';
import MyPulseWidget from '@/components/MyPulseWidget';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const statusBadge = {
  open:      { label: 'Open',     cls: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]' },
  accepted:  { label: 'Accepted', cls: 'bg-[#DBEAFE] text-[#1D4ED8] border-[#BFDBFE]' },
};

// Source-specific styling — distinct background tints + label so staff can
// tell apart Invoice / Draft / Token rows at a glance.
const sourceMeta = {
  invoice: { label: 'Invoice', rowBg: '',                 chip: 'bg-[#E0E7FF] text-[#4338CA] border-[#C7D2FE]' },
  draft:   { label: 'Draft',   rowBg: 'bg-[#FFFBEB]/70',  chip: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]' },
  token:   { label: 'Token',   rowBg: 'bg-[#ECFEFF]/70',  chip: 'bg-[#CFFAFE] text-[#0E7490] border-[#A5F3FC]' },
};

/**
 * My Work
 *
 * Each row = one invoice line item currently assigned to the logged-in
 * staff member. Completed work falls off — the focus is on the
 * actionable backlog.
 *
 * Actions per row:
 *   Accept     — assignee acknowledges the work (status → accepted)
 *   Complete   — assignee marks the work finished (status → completed; row leaves the list)
 *   Reassign   — pick another staff to hand it off (inline dropdown + Apply)
 *   Open       — jump to the invoice's edit page
 */
export default function MyWorkPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  // Inline reassign drawer state — keyed by `${invoice_id}-${item_idx}`.
  const [reassign, setReassign] = useState({});
  // Token-complete popup state. When a staffer marks a TOKEN item complete
  // we ask "create invoice now, or save as draft?" before stamping the
  // token. `tokenConvert` holds the pending row while the dialog is open.
  const [tokenConvert, setTokenConvert] = useState(null);
  const canCreateInvoice = useCan('sales', 'edit');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: s }] = await Promise.all([
        api.get('/work/my-work'),
        api.get('/work/staff'),
      ]);
      setRows(r || []);
      setStaff(s || []);
    } catch {
      toast.error('Failed to load my work');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Action helpers — drafts + invoices both support Accept / Complete /
  // Reassign. Tokens remain Open-only because completing a token without
  // an invoice has no business meaning yet.
  const _docId = (row) => row.invoice_id || row.draft_id || row.token_id || '';
  const _source = (row) => row.source || 'invoice';

  const accept = async (row) => {
    try {
      await api.post(`/work/items/${_docId(row)}/${row.item_idx}/accept?source=${_source(row)}`, { note: '' });
      toast.success(`Accepted ${row.service_name}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept');
    }
  };

  const complete = async (row) => {
    // Token completion has an extra step — surface the bill/draft popup
    // before stamping. Invoice + draft items go through directly.
    if (_source(row) === 'token') {
      setTokenConvert(row);
      return;
    }
    try {
      await api.post(`/work/items/${_docId(row)}/${row.item_idx}/complete?source=${_source(row)}`, { note: '' });
      toast.success(`Completed ${row.service_name}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete');
    }
  };

  // Token conversion handler — wired to the AlertDialog Yes/No buttons.
  // YES → backend stamps the token, then we navigate the user to the
  //       Invoice page with the token pre-loaded (existing flow).
  // NO  → backend creates a Draft with "Work completed, now create invoice"
  //       as the reason and stamps the token as completed.
  const handleTokenConvert = async (to) => {
    const row = tokenConvert;
    if (!row) return;
    const tokenId = row.token_id || row.invoice_id;
    setTokenConvert(null);
    try {
      // First mark the item as completed so it leaves My Work.
      await api.post(`/work/items/${tokenId}/${row.item_idx}/complete?source=token`, { note: '' });
      const r = await api.post(`/tokens/${tokenId}/complete-conversion`, { to });
      if (to === 'invoice') {
        toast.success('Token completed — opening invoice');
        navigate(`/invoices/create?token=${tokenId}`);
      } else {
        toast.success(`Saved as draft — ${row.customer_name || 'work'} completed`);
        refresh();
      }
      return r;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to convert token');
    }
  };

  const doReassign = async (row) => {
    const key = `${_source(row)}-${_docId(row)}-${row.item_idx}`;
    const target = reassign[key];
    if (!target?.assignTo) { toast.error('Pick a staff to reassign to'); return; }
    try {
      await api.post(`/work/items/${_docId(row)}/${row.item_idx}/assign?source=${_source(row)}`, {
        assigned_to: target.assignTo,
        note: target.note || '',
      });
      toast.success('Reassigned');
      setReassign(prev => { const n = { ...prev }; delete n[key]; return n; });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reassign');
    }
  };

  // Sort: accepted first (already in flight), then open. Within each, by invoice_date desc.
  const sorted = [...rows].sort((a, b) => {
    const orderA = a.assigned_status === 'accepted' ? 0 : 1;
    const orderB = b.assigned_status === 'accepted' ? 0 : 1;
    if (orderA !== orderB) return orderA - orderB;
    return (b.invoice_date || '').localeCompare(a.invoice_date || '');
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="my-work-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>My Work</h1>
          <p className="text-sm text-[#52525B]">Invoices, drafts and tokens assigned to you — accept, finish or hand off.</p>
        </div>
        <Badge variant="outline" className="rounded-lg text-xs h-8 px-3" data-testid="my-work-total">
          {rows.length} active
        </Badge>
      </div>

      {/* My-Pulse summary widget — staff only; admin already has /dashboard. */}
      {user?.role !== 'admin' && <MyPulseWidget />}

      {loading ? (
        <p className="text-center text-sm text-[#A1A1AA] py-12" data-testid="my-work-loading">Loading…</p>
      ) : sorted.length === 0 ? (
        <Card className="rounded-xl border-[#E4E4E7]">
          <CardContent className="py-16 text-center text-sm text-[#A1A1AA]" data-testid="my-work-empty">
            <Briefcase size={32} className="mx-auto text-[#A1A1AA] mb-2" />
            Nothing assigned right now. New items will show up here.
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-xl border-[#E4E4E7] overflow-hidden">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Voucher</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Customer</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Service</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Remarks</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Assigned By</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map(row => {
                  // For draft / token rows, build a unique key from the doc id
                  // (invoice_id is undefined). For Reassign/Accept/Complete
                  // we still only use the invoice path — drafts/tokens get
                  // an "Open" action only.
                  const source = row.source || 'invoice';
                  const sMeta = sourceMeta[source] || sourceMeta.invoice;
                  const docId = row.invoice_id || row.draft_id || row.token_id || '';
                  const key = `${source}-${docId}-${row.item_idx}`;
                  const isReassigning = !!reassign[key];
                  const b = statusBadge[row.assigned_status] || statusBadge.open;
                  const voucher = row.voucher_number || row.invoice_number || '—';
                  const openHref = source === 'invoice'
                    ? `/invoices/edit/${row.invoice_id}`
                    : source === 'draft'
                      ? `/invoices/create?draft=${row.draft_id}`
                      : `/invoices/create?token=${row.token_id}`;
                  return (
                    <TableRow
                      key={key}
                      className={`border-[#E4E4E7] ${sMeta.rowBg}`}
                      data-testid={`my-work-row-${voucher}-${row.item_idx}`}
                    >
                      <TableCell>
                        <div className="flex flex-row gap-1 items-center whitespace-nowrap">
                          <Badge className={`rounded-md text-[10px] capitalize ${b.cls}`} variant="outline">{b.label}</Badge>
                          <Badge className={`rounded-md text-[9px] uppercase tracking-wider ${sMeta.chip}`} variant="outline" data-testid={`source-badge-${voucher}`}>
                            {sMeta.label}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => navigate(openHref)}
                          className="text-xs font-mono text-[#3B82F6] hover:underline"
                          data-testid={`my-work-open-${voucher}`}
                        >
                          {voucher}
                        </button>
                        <p className="text-[10px] text-[#A1A1AA]">{row.invoice_date ? new Date(row.invoice_date).toLocaleDateString('en-IN') : '—'}</p>
                      </TableCell>
                      <TableCell className="text-xs font-medium">{row.customer_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          {row.service_link1 ? (
                            <a
                              href={row.service_link1}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`Open ${row.service_name} portal`}
                              data-testid={`mywork-link1-${row.source}-${row.invoice_id || row.draft_id || row.token_id}-${row.item_idx}`}
                              className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-[#E0F2FE] text-[#0369A1] hover:bg-[#BAE6FD] shrink-0"
                            >
                              <LinkSimple size={12} weight="bold" />
                            </a>
                          ) : null}
                          <div className="min-w-0">
                            <p className="text-xs font-medium">{row.service_name}</p>
                            <p className="text-[10px] text-[#A1A1AA] capitalize">{row.work_status || 'pending'}{row.followup ? ' · follow-up' : ''}{row.is_recurring ? ' · recurring' : ''}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-[#52525B] max-w-[180px] truncate" title={row.remark || ''}>
                        {row.remark || <span className="text-[#A1A1AA]">—</span>}
                      </TableCell>
                      <TableCell className="text-xs">{row.assigned_by_name || <span className="text-[#A1A1AA]">—</span>}</TableCell>
                      <TableCell>
                        {isReassigning ? (
                          <div className="flex items-center gap-1 justify-end" data-testid={`reassign-drawer-${voucher}`}>
                            <Select value={reassign[key]?.assignTo || ''} onValueChange={v => setReassign(prev => ({ ...prev, [key]: { ...(prev[key] || {}), assignTo: v === '__unassigned__' ? '' : v } }))}>
                              <SelectTrigger className="h-7 w-32 text-xs rounded-md" data-testid={`reassign-select-${voucher}`}>
                                <SelectValue placeholder="Pick staff…" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__unassigned__">Unassigned</SelectItem>
                                {staff.map(s => (
                                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              placeholder="Note (optional)"
                              className="h-7 w-32 text-xs rounded-md"
                              value={reassign[key]?.note || ''}
                              onChange={e => setReassign(prev => ({ ...prev, [key]: { ...(prev[key] || {}), note: e.target.value } }))}
                              data-testid={`reassign-note-${voucher}`}
                            />
                            <Button size="sm" className="h-7 px-2 text-xs bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-md" onClick={() => doReassign(row)} data-testid={`reassign-apply-${voucher}`}>
                              Apply
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setReassign(prev => { const n = { ...prev }; delete n[key]; return n; })}>
                              ✕
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            {row.assigned_note && (
                              <span title={row.assigned_note} className="text-[#A1A1AA] hover:text-[#3B82F6] cursor-help">
                                <ChatCircle size={14} />
                              </span>
                            )}
                            {row.assigned_status === 'open' && (
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-[#3B82F6] text-[#3B82F6] hover:bg-[#EFF6FF]" onClick={() => accept(row)} data-testid={`accept-${voucher}`}>
                                Accept
                              </Button>
                            )}
                            <Button size="sm" className="h-7 px-2 text-xs bg-[#10B981] text-white hover:bg-[#059669] hover:text-white rounded-md" onClick={() => complete(row)} data-testid={`complete-${voucher}`}>
                              <CheckCircle size={12} className="mr-1" /> Complete
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs text-[#52525B] hover:text-[#3B82F6]"
                              onClick={() => setReassign(prev => ({ ...prev, [key]: { assignTo: '', note: '' } }))}
                              title="Reassign"
                              data-testid={`reassign-${voucher}`}
                            >
                              <ArrowsLeftRight size={12} />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Token Complete confirmation — fires when the assignee clicks
          Complete on a Token row. Yes-button is hidden for staff who
          can't create/edit invoices; they only get the Draft path. */}
      <AlertDialog open={!!tokenConvert} onOpenChange={(o) => { if (!o) setTokenConvert(null); }}>
        <AlertDialogContent data-testid="token-convert-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Work complete &mdash; what next?</AlertDialogTitle>
            <AlertDialogDescription>
              {tokenConvert && (
                <>
                  Do you want to <strong>make a bill</strong> for{' '}
                  <span className="font-semibold text-[#0A0A0A]">{tokenConvert.customer_name}</span>
                  {' '}({tokenConvert.service_name}), or <strong>save it as a draft</strong> for the admin to bill later?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="token-convert-cancel">Cancel</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => handleTokenConvert('draft')}
              className="border-[#F59E0B] text-[#92400E] hover:bg-[#FFFBEB]"
              data-testid="token-convert-no-btn"
            >
              No — save as Draft
            </Button>
            {canCreateInvoice && (
              <AlertDialogAction
                onClick={() => handleTokenConvert('invoice')}
                className="bg-[#10B981] text-white hover:bg-[#059669]"
                data-testid="token-convert-yes-btn"
              >
                Yes — open Invoice
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
